import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Popover from '@mui/material/Popover';
import { getMediaUrl, useGetReactions } from '@onesource/services';

interface ReactorsAvatarStackProps {
  targetType: 'post' | 'comment';
  targetId: string;
  likeCount: number;
}

const MAX_AVATARS = 3;

export const ReactorsAvatarStack = ({ targetType, targetId, likeCount }: ReactorsAvatarStackProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Eagerly fetch when there are reactions so the avatar stack is visible
  // without the user having to interact. React Query dedupes per (target,id).
  const { data, isLoading } = useGetReactions(targetType, targetId, likeCount > 0);

  if (likeCount === 0) {
    return null;
  }

  const reactions = data?.reactions ?? [];
  const topReactors = reactions.slice(0, MAX_AVATARS);
  const remaining = Math.max(0, likeCount - topReactors.length);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (reactions.length === 0) {
      return;
    }
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          cursor: reactions.length > 0 ? 'pointer' : 'default',
        }}
      >
        {isLoading && topReactors.length === 0 ? (
          <CircularProgress size={12} />
        ) : (
          <>
            {topReactors.length > 0 && (
              <Box sx={{ display: 'flex' }}>
                {topReactors.map((r, idx) => (
                  <Avatar
                    key={r.user.id}
                    src={getMediaUrl(r.user.avatar)}
                    sx={{
                      width: 18,
                      height: 18,
                      fontSize: 9,
                      ml: idx > 0 ? '-6px' : 0,
                      border: '1.5px solid white',
                      zIndex: MAX_AVATARS - idx,
                    }}
                  >
                    {r.user.name?.charAt(0) || '?'}
                  </Avatar>
                ))}
              </Box>
            )}
            {remaining > 0 && (
              <Typography
                sx={{
                  fontFamily: 'Figtree, sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: 9, md: 9 },
                  color: '#333',
                }}
              >
                and {remaining} {remaining === 1 ? 'other' : 'others'}
              </Typography>
            )}
          </>
        )}
      </Box>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 260,
              maxHeight: 320,
              overflowY: 'auto',
              borderRadius: 2,
              boxShadow: '0 4px 18px rgba(0,0,0,0.12)',
            },
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          {reactions.map(r => (
            <Box key={r.user.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 0.5 }}>
              <Avatar src={getMediaUrl(r.user.avatar)} sx={{ width: 22, height: 22, fontSize: 11 }}>
                {r.user.name?.charAt(0) || '?'}
              </Avatar>
              <Typography sx={{ fontSize: 13, flex: 1, fontFamily: 'Figtree, sans-serif' }}>{r.user.name}</Typography>
              <Typography sx={{ fontSize: 14 }}>{r.emoji}</Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};
